import { useRef, useCallback, useEffect } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  RemoteParticipant,
  RemoteTrackPublication,
  RemoteTrack, VideoQuality,
} from 'livekit-client'
import type { WordTiming } from './useTextStream'

const AVATAR_PARTICIPANT_PREFIX = 'bey-avatar-agent'
const MAX_RETRIES = 3
// Per-attempt ceiling on the LiveKit handshake. Under heavy concurrency the
// connect can *hang* rather than error; without a timeout the retry loop would
// await a dead attempt forever and the candidate never joins (silent stall).
const CONNECT_TIMEOUT_MS = 12_000
const AUDIO_END_TIMEOUT_MS = 15_000

interface UseLiveKitOptions {
  onAvatarReady?: () => void
  // Fires once when the BEY avatar's *video* track is attached to the
  // <video> element — the actual moment the candidate sees the interviewer.
  // Narrower than onAvatarReady (which fires on first audio OR video track).
  // Used to gate the Start Interview button and hide the avatar-loading overlay.
  onAvatarVideoAttached?: () => void
  // Fires when the worker tells us BEY is at capacity (or BEY rejected the
  // connection for another reason). Candidate should see a "no interviewer
  // available" message and be blocked from starting the interview.
  // Issue #22, Phase 1.
  onAvatarUnavailable?: (reason: string) => void
  // Fires when BEY refused to start after our bounded retry budget was
  // exhausted (reason='bey_429_persistent'). The worker is alive in
  // audio-only fallback mode — the candidate can still proceed without
  // the avatar. Distinct from onAvatarUnavailable because that's a
  // dead-end; this isn't. Issue #22 follow-up.
  onAvatarDegraded?: () => void
  // Fires every ~BEY_QUEUE_STATUS_PUSH_INTERVAL seconds while the worker
  // is waiting in the BEY queue. position is 0-based; etaSeconds is a
  // rough estimate; queueDepth includes all waiters. Issue #22, Phase 2.
  onQueueStatus?: (position: number, etaSeconds: number, queueDepth: number) => void
  onDisconnected?: () => void
  onSpeechCompleted?: (segment: string) => void
  onWordTimings?: (text: string, words: WordTiming[]) => void
  onSpeechStarted?: () => void
}

export function useLiveKit({ onAvatarReady, onAvatarVideoAttached, onAvatarUnavailable, onAvatarDegraded, onQueueStatus, onDisconnected, onSpeechCompleted, onWordTimings, onSpeechStarted }: UseLiveKitOptions = {}) {
  const generationRef = useRef(0)
  const pendingConnectRef = useRef(new Set<() => void>())
  const pendingAudioWaitRef = useRef(new Set<() => void>())
  const roomRef = useRef<Room | null>(null)
  const videoElRef = useRef<HTMLVideoElement | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const retryCount = useRef(0)
  const avatarReadyRef = useRef(false)
  const avatarVideoAttachedRef = useRef(false)

  // Keep callback refs updated so room event handlers always call the latest version
  const onAvatarReadyRef = useRef(onAvatarReady)
  const onAvatarVideoAttachedRef = useRef(onAvatarVideoAttached)
  const onAvatarUnavailableRef = useRef(onAvatarUnavailable)
  const onAvatarDegradedRef = useRef(onAvatarDegraded)
  const onQueueStatusRef = useRef(onQueueStatus)
  const onDisconnectedRef = useRef(onDisconnected)
  const onSpeechCompletedRef = useRef(onSpeechCompleted)
  const onWordTimingsRef = useRef(onWordTimings)
  const onSpeechStartedRef = useRef(onSpeechStarted)
  useEffect(() => { onAvatarReadyRef.current = onAvatarReady })
  useEffect(() => { onAvatarVideoAttachedRef.current = onAvatarVideoAttached })
  useEffect(() => { onAvatarUnavailableRef.current = onAvatarUnavailable })
  useEffect(() => { onAvatarDegradedRef.current = onAvatarDegraded })
  useEffect(() => { onQueueStatusRef.current = onQueueStatus })
  useEffect(() => { onDisconnectedRef.current = onDisconnected })
  useEffect(() => { onSpeechCompletedRef.current = onSpeechCompleted })
  useEffect(() => { onWordTimingsRef.current = onWordTimings })
  useEffect(() => { onSpeechStartedRef.current = onSpeechStarted })

  // Created eagerly on connect (not lazily on first track): the settings
  // panel's speaker selection calls setSinkId on this element — before the
  // first avatar audio landed, the switch was a silent no-op.
  const ensureAudioEl = useCallback((): HTMLAudioElement => {
    if (!audioElRef.current) {
      audioElRef.current = document.createElement('audio')
      audioElRef.current.autoplay = true
      audioElRef.current.style.display = 'none'
      document.body.appendChild(audioElRef.current)
    }
    return audioElRef.current
  }, [])

  const attachAvatarTrack = useCallback(
    (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (!publication.track) return

      const isBeyAvatar = participant.identity === AVATAR_PARTICIPANT_PREFIX

      if (isBeyAvatar) {
        // BEY avatar participant: attach video + audio, then signal ready
        if (publication.kind === Track.Kind.Video && videoElRef.current) {
          publication.setVideoQuality(VideoQuality.HIGH)
          publication.track.attach(videoElRef.current)
          if (!avatarVideoAttachedRef.current) {
            avatarVideoAttachedRef.current = true
            console.log('[LiveKit] bey-avatar video attached, firing onAvatarVideoAttached')
            onAvatarVideoAttachedRef.current?.()
          }
        }
        if (publication.kind === Track.Kind.Audio) {
          publication.track.attach(ensureAudioEl())
        }
        if (!avatarReadyRef.current) {
          avatarReadyRef.current = true
          console.log('[LiveKit] bey-avatar-agent ready, firing onAvatarReady')
          onAvatarReadyRef.current?.()
        }
      } else {
        // Simulated agent or other participant: attach audio only for TTS playback
        if (publication.kind === Track.Kind.Audio) {
          console.log('[LiveKit] attaching audio from simulated agent:', participant.identity)
          publication.track.attach(ensureAudioEl())
        }
      }
    },
    [ensureAudioEl]
  )

  // Autoplay recovery (Safari/iOS): our hidden <audio> element is created
  // programmatically, so its internal play() never runs inside a user
  // gesture and strict autoplay policies mute the avatar. LiveKit signals
  // this via AudioPlaybackStatusChanged; the fix is calling room.startAudio()
  // from within the NEXT user gesture. Listeners are one-shot and re-armed
  // if playback gets blocked again.
  const audioResumeCleanupRef = useRef<(() => void) | null>(null)

  const armAudioResume = useCallback((room: Room) => {
    if (audioResumeCleanupRef.current) return // already armed
    const cleanup = () => {
      document.removeEventListener('pointerdown', resume)
      document.removeEventListener('keydown', resume)
      audioResumeCleanupRef.current = null
    }
    const resume = () => {
      cleanup()
      room.startAudio().catch((err) => console.warn('[LiveKit] startAudio failed:', err))
    }
    document.addEventListener('pointerdown', resume)
    document.addEventListener('keydown', resume)
    audioResumeCleanupRef.current = cleanup
  }, [])

  const connect = useCallback(
    async (url: string, token: string, generation: number) => {
      if (generationRef.current !== generation) throw new Error('Connection cancelled')
      // Re-setup guard: tear down any previous room before creating a new
      // one, otherwise the old room stays connected in the background
      // (double audio, leaked listeners) with no ref left to clean it up.
      if (roomRef.current) {
        const stale = roomRef.current
        roomRef.current = null
        await stale.disconnect().catch(() => {})
      }
      if (generationRef.current !== generation) throw new Error('Connection cancelled')
      audioResumeCleanupRef.current?.()

      // H.264 publishing matches our TRACK_EGRESS pipeline expectations:
      // egress writes raw H.264 directly into MP4, and ffmpeg `-c:v copy` muxes it
      // into the final {segment}_user.mp4 with no transcoding. With VP8 (the libwebrtc
      // default) the raw file would be .ivf and break the .mp4 contract the admin UI reads.
      const room = new Room({
        publishDefaults: { videoCodec: 'h264' },
      })
      roomRef.current = room

      room.on(RoomEvent.TrackSubscribed, (_track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (roomRef.current !== room) return
        console.log('[LiveKit] TrackSubscribed:', participant.identity, pub.kind)
        attachAvatarTrack(pub, participant)
      })

      room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        if (roomRef.current !== room) return
        participant.trackPublications.forEach((pub) => {
          if (pub.track) attachAvatarTrack(pub, participant)
        })
      })

      room.on(RoomEvent.Disconnected, () => {
        if (roomRef.current === room) onDisconnectedRef.current?.()
      })

      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (roomRef.current !== room) return
        if (!room.canPlaybackAudio) {
          console.warn('[LiveKit] audio playback blocked by autoplay policy — waiting for user gesture')
          armAudioResume(room)
        }
      })

      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        if (roomRef.current !== room) return
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload))
          console.log('[LiveKit] DataReceived:', msg.type)
          if (msg.type === 'avatar_ready' && !avatarReadyRef.current) {
            avatarReadyRef.current = true
            console.log('[LiveKit] avatar_ready data event, firing onAvatarReady')
            onAvatarReadyRef.current?.()
          }
          if (msg.type === 'speech_completed') {
            onSpeechCompletedRef.current?.(msg.segment ?? '')
          }
          if (msg.type === 'word_timings' && Array.isArray(msg.words)) {
            onWordTimingsRef.current?.(msg.text ?? '', msg.words)
          }
          if (msg.type === 'speech_started') {
            onSpeechStartedRef.current?.()
          }
          if (msg.type === 'avatar_unavailable') {
            console.warn('[LiveKit] avatar_unavailable:', msg.reason)
            // bey_429_persistent means BEY refused after our retry budget;
            // the worker is alive in audio-only mode, so route to the
            // degraded path (candidate can continue without avatar). All
            // other reasons are dead-ends — keep the unavailable path.
            if (msg.reason === 'bey_429_persistent') {
              onAvatarDegradedRef.current?.()
            } else {
              onAvatarUnavailableRef.current?.(msg.reason ?? 'unknown')
            }
          }
          if (msg.type === 'queue_status' && typeof msg.position === 'number') {
            onQueueStatusRef.current?.(
              msg.position,
              typeof msg.etaSeconds === 'number' ? msg.etaSeconds : 0,
              typeof msg.queueDepth === 'number' ? msg.queueDepth : 0,
            )
          }
          if (msg.type === 'session_timeout') {
            // The worker's no-join watchdog gave up and is shutting down.
            // Reaches us only if we're in the room already (edge case, since
            // the watchdog arms on our join) — treat it as a disconnect so a
            // running interview ends cleanly instead of freezing on a
            // departing avatar.
            console.warn('[LiveKit] session_timeout from worker:', msg.reason)
            onDisconnectedRef.current?.()
          }
        } catch { /* ignore */ }
      })

      // Create the hidden audio sink now so speaker selection (setSinkId)
      // works before the first avatar audio track attaches.
      ensureAudioEl()

      console.log('[LiveKit] connecting to', url)
      await room.connect(url, token, { autoSubscribe: true })
      if (generationRef.current !== generation || roomRef.current !== room) {
        room.removeAllListeners()
        await room.disconnect()
        throw new Error('Connection cancelled')
      }
      console.log('[LiveKit] connected, participants:', [...room.remoteParticipants.keys()])


      // Check already-connected participants
      room.remoteParticipants.forEach((participant) => {
        console.log('[LiveKit] existing participant:', participant.identity)
        participant.trackPublications.forEach((pub) => {
          if (pub.track) attachAvatarTrack(pub, participant)
        })
      })
    },
    [attachAvatarTrack, armAudioResume, ensureAudioEl]
  )

  const connectWithRetry = useCallback(
    async (url: string, token: string): Promise<void> => {
      const generation = ++generationRef.current
      retryCount.current = 0
      while (retryCount.current <= MAX_RETRIES) {
        if (generationRef.current !== generation) throw new Error('Connection cancelled')
        let timeout: ReturnType<typeof setTimeout> | undefined
        let cancel = () => {}
        const cancelled = new Promise<never>((_, reject) => {
          cancel = () => reject(new Error('Connection cancelled'))
          pendingConnectRef.current.add(cancel)
        })
        try {
          // Race the connect against a timeout so a hung handshake is
          // abandoned and retried rather than awaited forever. connect()
          // tears down any half-open room on its next call, so the abandoned
          // attempt doesn't leak.
          await Promise.race([
            connect(url, token, generation),
            cancelled,
            new Promise<never>((_, reject) =>
              timeout = setTimeout(() => reject(new Error('connect timeout')), CONNECT_TIMEOUT_MS)
            ),
          ])
          retryCount.current = 0
          return
        } catch (err) {
          if (generationRef.current !== generation) throw err
          const stale = roomRef.current
          roomRef.current = null
          stale?.removeAllListeners()
          await stale?.disconnect().catch(() => {})
          retryCount.current++
          if (retryCount.current > MAX_RETRIES) throw err
          // Exponential backoff with jitter — staggers many candidates
          // reconnecting at once (thundering herd) so they don't re-congest
          // the server in lockstep under concurrency.
          const delay = 1000 * 2 ** (retryCount.current - 1) + Math.random() * 500
          console.warn(`[LiveKit] connect attempt ${retryCount.current} failed, retrying in ${delay.toFixed(0)}ms`, err)
          await new Promise<void>((resolve, reject) => {
            const stop = () => { clearTimeout(timer); reject(new Error('Connection cancelled')) }
            const timer = setTimeout(() => { pendingConnectRef.current.delete(stop); resolve() }, delay)
            pendingConnectRef.current.add(stop)
          })
        } finally {
          if (timeout) clearTimeout(timeout)
          pendingConnectRef.current.delete(cancel)
        }
      }
    },
    [connect]
  )

  // Re-attach already-subscribed avatar tracks (call when video element mounts late)
  const reattachTracks = useCallback(() => {
    const room = roomRef.current
    if (!room) return
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => {
        if (pub.track) attachAvatarTrack(pub, participant)
      })
    })
  }, [attachAvatarTrack])

  // Publish all tracks from an existing MediaStream (camera + mic) to the room for egress recording.
  // Source must be explicitly set — raw MediaStreamTrack defaults to UNKNOWN and egress ignores it.
  const publishStream = useCallback(async (stream: MediaStream) => {
    const room = roomRef.current
    if (!room) return
    try {
      for (const track of stream.getTracks()) {
        if (roomRef.current !== room) return
        const source = track.kind === 'video' ? Track.Source.Camera : Track.Source.Microphone
        await room.localParticipant.publishTrack(track, {
          source,
          ...(track.kind === 'video' ? {
            videoCodec: 'h264' as const,
            simulcast: false,
            videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
          } : {}),
        })
      }
      console.log('[LiveKit] local camera+audio published for recording')
    } catch (err) {
      console.warn('[LiveKit] failed to publish local tracks:', err)
    }
  }, [])

  // Swap the published camera/microphone track in place (device switch during
  // a running interview). replaceTrack keeps the same RTP sender, so the
  // server-side egress recording follows the new device seamlessly — without
  // this, egress keeps recording the old, stopped track (silence/black frame).
  const replaceLocalTrack = useCallback(async (newTrack: MediaStreamTrack) => {
    const room = roomRef.current
    if (!room) return
    const source = newTrack.kind === 'video' ? Track.Source.Camera : Track.Source.Microphone
    const pub = room.localParticipant.getTrackPublication(source)
    if (pub?.track) {
      await pub.track.replaceTrack(newTrack)
      console.log(`[LiveKit] replaced published ${newTrack.kind} track`)
    }
  }, [])

  const disconnect = useCallback(async () => {
    generationRef.current++
    pendingConnectRef.current.forEach((cancel) => cancel())
    pendingConnectRef.current.clear()
    pendingAudioWaitRef.current.forEach((finish) => finish())
    pendingAudioWaitRef.current.clear()
    audioResumeCleanupRef.current?.()
    const room = roomRef.current
    roomRef.current = null
    // Detach remote tracks explicitly — otherwise the <video> element keeps
    // rendering the last decoded frame after the room is gone.
    room?.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => {
        pub.track?.detach()
      })
    })
    room?.removeAllListeners()
    if (audioElRef.current) {
      audioElRef.current.remove()
      audioElRef.current = null
    }
    avatarReadyRef.current = false
    await room?.disconnect()
  }, [])

  const waitForAudioEnd = useCallback((timeoutMs: number = AUDIO_END_TIMEOUT_MS): Promise<void> => {
    if (timeoutMs <= 0) {
      console.log('[LiveKit] waitForAudioEnd: no remaining time, resolving immediately')
      return Promise.resolve()
    }
    const el = audioElRef.current
    if (!el || el.ended || el.paused) {
      console.log('[LiveKit] waitForAudioEnd: resolving immediately (no element / ended / paused)')
      return Promise.resolve()
    }
    console.log(`[LiveKit] waitForAudioEnd: waiting up to ${timeoutMs.toFixed(0)}ms`)
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer)
        el.removeEventListener('ended', onEnded)
        pendingAudioWaitRef.current.delete(finish)
        resolve()
      }
      const timer = setTimeout(finish, timeoutMs)
      function onEnded() { finish() }
      pendingAudioWaitRef.current.add(finish)
      el.addEventListener('ended', onEnded)
    })
  }, [])

  return { connect: connectWithRetry, disconnect, reattachTracks, publishStream, replaceLocalTrack, videoElRef, audioElRef, avatarReadyRef, waitForAudioEnd }
}
